import api from './api';
import {
  normalizePayCadence,
  normalizePaymentMode,
  PAY_DAY_DEFAULT,
} from '../constants/EmployeePaymentModes';
import {
  collectMembershipPotPeriods,
  parseMembershipMonthlySharePeriod,
} from '../utils/membershipPayrollPotPeriods';
import {
  DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL,
  normalizeMembershipRevenueDistributionModel,
} from '../constants/MembershipRevenueDistributionModel';

const withStoreId = (storeId) => (storeId ? { _storeId: storeId } : undefined);

const appendDateRangeParams = (params, dateRange) => {
  params.append('dateRange.from', dateRange.from);
  params.append('dateRange.to', dateRange.to);
};

const appendTeamMemberIds = (params, teamMemberIds) => {
  if (!teamMemberIds?.length) return;
  teamMemberIds.forEach((teamMemberId) => {
    params.append('teamMemberIds', teamMemberId);
  });
};

const normalizeMembershipRevenueSettings = (data) => ({
  storeMarginPercent: data?.storeMarginPercent ?? data?.StoreMarginPercent ?? 0,
  membershipSharePayDayOfMonth: data?.membershipSharePayDayOfMonth ?? data?.MembershipSharePayDayOfMonth ?? PAY_DAY_DEFAULT,
  distributionModel: normalizeMembershipRevenueDistributionModel(
    data?.distributionModel ?? data?.DistributionModel ?? DEFAULT_MEMBERSHIP_REVENUE_DISTRIBUTION_MODEL,
  ),
  nextPayDate: data?.nextPayDate ?? data?.NextPayDate ?? null,
  pendingUndistributedBarberPoolAmount:
    data?.pendingUndistributedBarberPoolAmount ?? data?.PendingUndistributedBarberPoolAmount ?? 0,
});

const normalizeCompensation = (data) => ({
  teamMemberId: data?.teamMemberId ?? data?.TeamMemberId,
  paymentMode: normalizePaymentMode(data?.paymentMode ?? data?.PaymentMode),
  fixedMonthlySalary: data?.fixedMonthlySalary ?? data?.FixedMonthlySalary ?? null,
  commissionRatePercent: data?.commissionRatePercent ?? data?.CommissionRatePercent ?? null,
  minimumMonthlySalary: data?.minimumMonthlySalary ?? data?.MinimumMonthlySalary ?? null,
  effectiveFrom: data?.effectiveFrom ?? data?.EffectiveFrom ?? null,
  payCadence: normalizePayCadence(data?.payCadence ?? data?.PayCadence),
  payDayOfMonth: data?.payDayOfMonth ?? data?.PayDayOfMonth ?? PAY_DAY_DEFAULT,
  payDayOfWeek: data?.payDayOfWeek ?? data?.PayDayOfWeek ?? 1,
  applyDiscountDeductions: Boolean(data?.applyDiscountDeductions ?? data?.ApplyDiscountDeductions),
});

const normalizeBreakdownLine = (line) => ({
  id: line?.id ?? line?.Id,
  sourceType: line?.sourceType ?? line?.SourceType ?? 0,
  sourceId: line?.sourceId ?? line?.SourceId ?? '',
  grossAmount: line?.grossAmount ?? line?.GrossAmount ?? 0,
  storeMarginAmount: line?.storeMarginAmount ?? line?.StoreMarginAmount ?? 0,
  netCommissionBaseAmount: line?.netCommissionBaseAmount ?? line?.NetCommissionBaseAmount ?? 0,
  recognitionDate: line?.recognitionDate ?? line?.RecognitionDate ?? null,
});

const normalizeLedgerStatus = (status) => {
  if (status == null) return null;
  return String(status).replace(/\s+/g, '').toLowerCase();
};

const normalizeMembershipBreakdownLine = (line) => ({
  id: line?.id ?? line?.Id,
  sourceId: line?.sourceId ?? line?.SourceId ?? '',
  shareAmount: line?.shareAmount ?? line?.ShareAmount ?? 0,
  recognitionDate: line?.recognitionDate ?? line?.RecognitionDate ?? null,
  referenceCalendarYear: line?.referenceCalendarYear ?? line?.ReferenceCalendarYear ?? null,
  referenceCalendarMonth: line?.referenceCalendarMonth ?? line?.ReferenceCalendarMonth ?? null,
  status: normalizeLedgerStatus(line?.status ?? line?.Status),
});

const normalizeServiceMemberReport = (member) => ({
  teamMemberId: member?.teamMemberId ?? member?.TeamMemberId,
  paymentMode: normalizePaymentMode(member?.paymentMode ?? member?.PaymentMode),
  payCadence: normalizePayCadence(member?.payCadence ?? member?.PayCadence),
  fixedSalary: member?.fixedSalary ?? member?.FixedSalary ?? 0,
  commissionBase: member?.commissionBase ?? member?.CommissionBase ?? 0,
  commissionRatePercent: member?.commissionRatePercent ?? member?.CommissionRatePercent ?? 0,
  commissionAmount: member?.commissionAmount ?? member?.CommissionAmount ?? 0,
  minimumApplied: member?.minimumApplied ?? member?.MinimumApplied ?? false,
  totalPayout: member?.totalPayout ?? member?.TotalPayout ?? 0,
  breakdown: (member?.breakdown ?? member?.Breakdown ?? []).map(normalizeBreakdownLine),
});

const normalizeServicePayrollReport = (data) => {
  const grossBilled = data?.storeTotals?.grossBilled ?? data?.StoreTotals?.GrossBilled ?? 0;
  const totalPayout = data?.storeTotals?.totalPayout ?? data?.StoreTotals?.TotalPayout ?? 0;
  const storeGain = data?.storeTotals?.storeGain ?? data?.StoreTotals?.StoreGain ?? (grossBilled - totalPayout);

  return {
    dateRangeFrom: data?.dateRangeFrom ?? data?.DateRangeFrom ?? null,
    dateRangeTo: data?.dateRangeTo ?? data?.DateRangeTo ?? null,
    members: (data?.members ?? data?.Members ?? []).map(normalizeServiceMemberReport),
    storeTotals: {
      grossBilled,
      totalCommissionsPaid: data?.storeTotals?.totalCommissionsPaid ?? data?.StoreTotals?.TotalCommissionsPaid ?? 0,
      totalPayout,
      storeGain,
    },
  };
};

const normalizeMembershipMemberReport = (member) => ({
  teamMemberId: member?.teamMemberId ?? member?.TeamMemberId,
  totalShareAmount: member?.totalShareAmount ?? member?.TotalShareAmount ?? 0,
  breakdown: (member?.breakdown ?? member?.Breakdown ?? []).map(normalizeMembershipBreakdownLine),
});

const normalizeMembershipPayrollReport = (data) => ({
  dateRangeFrom: data?.dateRangeFrom ?? data?.DateRangeFrom ?? null,
  dateRangeTo: data?.dateRangeTo ?? data?.DateRangeTo ?? null,
  members: (data?.members ?? data?.Members ?? []).map(normalizeMembershipMemberReport),
  storeTotals: {
    grossBilled: data?.storeTotals?.grossBilled ?? data?.StoreTotals?.GrossBilled ?? 0,
    storeMarginRetained: data?.storeTotals?.storeMarginRetained ?? data?.StoreTotals?.StoreMarginRetained ?? 0,
    barberPool: data?.storeTotals?.barberPool ?? data?.StoreTotals?.BarberPool ?? 0,
    totalSharesPaid: data?.storeTotals?.totalSharesPaid ?? data?.StoreTotals?.TotalSharesPaid ?? 0,
  },
});

const normalizeAppointmentDetailLine = (line) => ({
  appointmentId: line?.appointmentId ?? line?.AppointmentId ?? null,
  appointmentDate: line?.appointmentDate ?? line?.AppointmentDate ?? null,
  customerName: line?.customerName ?? line?.CustomerName ?? '',
  executedServices: line?.executedServices ?? line?.ExecutedServices ?? '',
  totalPrice: line?.totalPrice ?? line?.TotalPrice ?? 0,
  additionalCostAmount: line?.additionalCostAmount ?? line?.AdditionalCostAmount ?? 0,
  employeeAdditionalCostShareAmount:
    line?.employeeAdditionalCostShareAmount ?? line?.EmployeeAdditionalCostShareAmount ?? 0,
  sourceTypes: line?.sourceTypes ?? line?.SourceTypes ?? [],
});

const normalizeMembershipPotMemberShare = (member) => ({
  teamMemberId: member?.teamMemberId ?? member?.TeamMemberId,
  teamMemberName: member?.teamMemberName ?? member?.TeamMemberName ?? '',
  points: member?.points ?? member?.Points ?? 0,
  sharePercent: member?.sharePercent ?? member?.SharePercent ?? 0,
  shareAmount: member?.shareAmount ?? member?.ShareAmount ?? 0,
});

const normalizeMembershipMonthlyPotDetail = (data) => ({
  storeId: data?.storeId ?? data?.StoreId,
  calendarYear: data?.calendarYear ?? data?.CalendarYear ?? 0,
  calendarMonth: data?.calendarMonth ?? data?.CalendarMonth ?? 0,
  grossAmount: data?.grossAmount ?? data?.GrossAmount ?? 0,
  storeMarginAmount: data?.storeMarginAmount ?? data?.StoreMarginAmount ?? 0,
  barberPoolAmount: data?.barberPoolAmount ?? data?.BarberPoolAmount ?? 0,
  totalPoints: data?.totalPoints ?? data?.TotalPoints ?? 0,
  allocationMethod: data?.allocationMethod ?? data?.AllocationMethod ?? '',
  memberShares: (data?.memberShares ?? data?.MemberShares ?? []).map(normalizeMembershipPotMemberShare),
});

export { collectMembershipPotPeriods, parseMembershipMonthlySharePeriod };

const normalizeAppointmentDetails = (data) => ({
  teamMemberId: data?.teamMemberId ?? data?.TeamMemberId,
  teamMemberName: data?.teamMemberName ?? data?.TeamMemberName ?? '',
  dateRangeFrom: data?.dateRangeFrom ?? data?.DateRangeFrom ?? null,
  dateRangeTo: data?.dateRangeTo ?? data?.DateRangeTo ?? null,
  months: (data?.months ?? data?.Months ?? []).map((month) => ({
    year: month?.year ?? month?.Year ?? 0,
    month: month?.month ?? month?.Month ?? 0,
    appointments: (month?.appointments ?? month?.Appointments ?? []).map(normalizeAppointmentDetailLine),
  })),
});

export const getMembershipRevenueSettings = async (storeId) => {
  const response = await api.get(`/stores/${storeId}/membership-revenue-settings`, withStoreId(storeId));
  return normalizeMembershipRevenueSettings(response.data);
};

export const updateMembershipRevenueSettings = async (storeId, payload) => {
  await api.put(
    `/stores/${storeId}/membership-revenue-settings`,
    {
      storeMarginPercent: payload.storeMarginPercent,
      membershipSharePayDayOfMonth: payload.membershipSharePayDayOfMonth,
      distributionModel: payload.distributionModel,
    },
    withStoreId(storeId),
  );
};

export const getTeamMemberCompensation = async (storeId, teamMemberId) => {
  const response = await api.get(
    `/stores/${storeId}/team-members/${teamMemberId}/compensation`,
    withStoreId(storeId),
  );
  return normalizeCompensation(response.data);
};

export const updateTeamMemberCompensation = async (storeId, teamMemberId, payload) => {
  await api.put(
    `/stores/${storeId}/team-members/${teamMemberId}/compensation`,
    {
      paymentMode: payload.paymentMode,
      fixedMonthlySalary: payload.fixedMonthlySalary,
      commissionRatePercent: payload.commissionRatePercent,
      minimumMonthlySalary: payload.minimumMonthlySalary,
      effectiveFrom: payload.effectiveFrom,
      payCadence: payload.payCadence,
      payDayOfMonth: payload.payDayOfMonth,
      payDayOfWeek: payload.payDayOfWeek,
      applyDiscountDeductions: Boolean(payload.applyDiscountDeductions),
    },
    withStoreId(storeId),
  );
};

export const getStoreServicePayrollReport = async (storeId, dateRange, teamMemberIds) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);
  appendTeamMemberIds(params, teamMemberIds);

  const response = await api.get(
    `/stores/${storeId}/employee-payments/service-payroll/reports?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeServicePayrollReport(response.data);
};

export const getStoreMembershipPayrollReport = async (storeId, dateRange, teamMemberIds) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);
  appendTeamMemberIds(params, teamMemberIds);

  const response = await api.get(
    `/stores/${storeId}/employee-payments/membership-payroll/reports?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeMembershipPayrollReport(response.data);
};

export const getTeamMemberServicePayrollReport = async (storeId, teamMemberId, dateRange) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);

  const response = await api.get(
    `/stores/${storeId}/team-members/${teamMemberId}/employee-payments/service-payroll/reports?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeServicePayrollReport(response.data);
};

export const getTeamMemberMembershipPayrollReport = async (storeId, teamMemberId, dateRange) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);

  const response = await api.get(
    `/stores/${storeId}/team-members/${teamMemberId}/employee-payments/membership-payroll/reports?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeMembershipPayrollReport(response.data);
};

export const getTeamMemberPayrollAppointmentDetails = async (storeId, teamMemberId, dateRange) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);

  const response = await api.get(
    `/stores/${storeId}/team-members/${teamMemberId}/employee-payments/appointment-details?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeAppointmentDetails(response.data);
};

export const getMembershipPeriodUsagePayoutDetail = async (storeId, dateRange) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);

  const response = await api.get(
    `/stores/${storeId}/employee-payments/membership-period-payouts?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeMembershipPeriodUsagePayoutDetail(response.data);
};

const normalizeMembershipPeriodMemberShare = (share) => ({
  teamMemberId: share?.teamMemberId ?? share?.TeamMemberId,
  teamMemberName: share?.teamMemberName ?? share?.TeamMemberName ?? '',
  redemptionWeight: share?.redemptionWeight ?? share?.RedemptionWeight ?? 0,
  shareAmount: share?.shareAmount ?? share?.ShareAmount ?? 0,
});

const normalizeMembershipPeriodUsagePayoutPeriod = (period) => ({
  customerMembershipId: period?.customerMembershipId ?? period?.CustomerMembershipId,
  customerDisplayName: period?.customerDisplayName ?? period?.CustomerDisplayName ?? '',
  planName: period?.planName ?? period?.PlanName ?? {},
  billingPeriodStartUtc: period?.billingPeriodStartUtc ?? period?.BillingPeriodStartUtc,
  billingPeriodEndUtc: period?.billingPeriodEndUtc ?? period?.BillingPeriodEndUtc,
  recognitionDate: period?.recognitionDate ?? period?.RecognitionDate,
  entitlementType: period?.entitlementType ?? period?.EntitlementType,
  allowanceQuantity: period?.allowanceQuantity ?? period?.AllowanceQuantity ?? null,
  totalRedemptions: period?.totalRedemptions ?? period?.TotalRedemptions ?? 0,
  grossAmount: period?.grossAmount ?? period?.GrossAmount ?? 0,
  storeMarginAmount: period?.storeMarginAmount ?? period?.StoreMarginAmount ?? 0,
  barberPoolAmount: period?.barberPoolAmount ?? period?.BarberPoolAmount ?? 0,
  distributedAmount: period?.distributedAmount ?? period?.DistributedAmount ?? 0,
  unallocatedAmount: period?.unallocatedAmount ?? period?.UnallocatedAmount ?? 0,
  memberShares: (period?.memberShares ?? period?.MemberShares ?? []).map(normalizeMembershipPeriodMemberShare),
});

const normalizeMembershipPeriodUsagePayoutDetail = (data) => ({
  storeId: data?.storeId ?? data?.StoreId,
  dateRangeFrom: data?.dateRangeFrom ?? data?.DateRangeFrom ?? null,
  dateRangeTo: data?.dateRangeTo ?? data?.DateRangeTo ?? null,
  grossAmount: data?.grossAmount ?? data?.GrossAmount ?? 0,
  storeMarginAmount: data?.storeMarginAmount ?? data?.StoreMarginAmount ?? 0,
  barberPoolAmount: data?.barberPoolAmount ?? data?.BarberPoolAmount ?? 0,
  distributedAmount: data?.distributedAmount ?? data?.DistributedAmount ?? 0,
  unallocatedAmount: data?.unallocatedAmount ?? data?.UnallocatedAmount ?? 0,
  periods: (data?.periods ?? data?.Periods ?? []).map(normalizeMembershipPeriodUsagePayoutPeriod),
});

export const getMembershipMonthlyPotDetail = async (storeId, calendarYear, calendarMonth) => {
  const params = new URLSearchParams({
    year: String(calendarYear),
    month: String(calendarMonth),
  });

  const response = await api.get(
    `/stores/${storeId}/employee-payments/membership-pot?${params.toString()}`,
    withStoreId(storeId),
  );
  return normalizeMembershipMonthlyPotDetail(response.data);
};

export const getStoreCashFlowReport = async (storeId, dateRange) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, dateRange);

  const response = await api.get(
    `/stores/${storeId}/employee-payments/cash-flow/reports?${params.toString()}`,
    withStoreId(storeId),
  );

  const data = response.data || {};
  return {
    totalReceived: data.totalReceived ?? data.TotalReceived ?? 0,
    cashier: data.cashier ?? data.Cashier ?? 0,
    bank: data.bank ?? data.Bank ?? 0,
    other: data.other ?? data.Other ?? 0,
    byPaymentMethod: (data.byPaymentMethod ?? data.ByPaymentMethod ?? []).map((item) => ({
      paymentMethod: item.paymentMethod ?? item.PaymentMethod,
      amountReceived: item.amountReceived ?? item.AmountReceived ?? 0,
    })),
    bySourceType: (data.bySourceType ?? data.BySourceType ?? []).map((item) => ({
      sourceType: item.sourceType ?? item.SourceType,
      amountReceived: item.amountReceived ?? item.AmountReceived ?? 0,
    })),
  };
};

const employeePaymentService = {
  getMembershipRevenueSettings,
  updateMembershipRevenueSettings,
  getTeamMemberCompensation,
  updateTeamMemberCompensation,
  getStoreServicePayrollReport,
  getStoreMembershipPayrollReport,
  getStoreCashFlowReport,
  getTeamMemberServicePayrollReport,
  getTeamMemberMembershipPayrollReport,
  getTeamMemberPayrollAppointmentDetails,
  getMembershipMonthlyPotDetail,
  getMembershipPeriodUsagePayoutDetail,
  parseMembershipMonthlySharePeriod,
  collectMembershipPotPeriods,
};

export default employeePaymentService;
